// ============================================================================
// CANCUN100 — PDFClienteAnterior.jsx (APP 2/32c: la versión de antes del cupón 2.0, mientras conviven las dos) (V4 — soporta reserva, cotización y ajuste)
// ============================================================================
// Tipos soportados (prop `tipo`):
//   - 'reserva'    → comprobante normal de venta (DEFAULT)
//   - 'cotizacion' → propuesta con vigencia 24h
//   - 'ajuste'     → cupón de pago parcial con histórico de abonos
//
// Para tipo='ajuste', data.resumen debe traer:
//   {
//     total: 9213,
//     historial: [
//       { etiqueta: 'Depósito inicial', monto: 3000, fecha: '15-03-2026' },
//       { etiqueta: 'Pago 1',           monto: 2000, fecha: '20-03-2026' },
//       { etiqueta: 'Pago 2',           monto: 1000, fecha: '04-06-2026' }
//     ],
//     saldo: 3213,
//     moneda: 'MXN'
//   }
// ============================================================================

import React from 'react';
import {
  Document, Page, Text, View, Image, StyleSheet,
  Svg, Path, Rect, Defs, LinearGradient, Stop,
} from '@react-pdf/renderer';
import './pdfFonts';
import { COLORS, fmtMXN } from './pdfStyles';
import { LOGO_BASE64 } from './logoBase64';
import { contactoPorCanal, DIRECCION } from '../contacto';
import gradientPng from './gradient-pink-orange.png';

// ── Colores custom hotel/vuelo (paridad con el mockup Variante C) ──
const HOTEL_A = '#7c5cff', HOTEL_B = '#a78bfa';
const VUELO_A = '#0ea5e9', VUELO_B = '#7dd3fc';
const CONTENT_W = 536; // ancho útil (LETTER 612 - 2*38 padding)

// ── Iconos SVG de trazo fino (estilo lucide; NUNCA emojis) ──
const Stroke = ({ d, stroke, size = 13, sw = 2 }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    {(Array.isArray(d) ? d : [d]).map((p, i) => (
      <Path key={i} d={p} stroke={stroke} strokeWidth={sw} fill="none" strokeLinecap="round" strokeLinejoin="round" />
    ))}
  </Svg>
);
const IconMaleta = ({ size = 14, stroke = COLORS.rosa }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Rect x="5" y="7" width="14" height="13" rx="2" stroke={stroke} strokeWidth="2" fill="none" />
    <Path d="M9 7V5a3 3 0 0 1 6 0v2 M9 12v4 M15 12v4" stroke={stroke} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);
const IconHotelEdif = ({ size = 14, stroke = HOTEL_A }) => (
  <Stroke size={size} stroke={stroke} d={['M3 21V8l9-5 9 5v13', 'M9 21v-6h6v6', 'M2 21h20']} />
);
const IconCama = ({ size = 15, stroke = '#fff' }) => (
  <Stroke size={size} stroke={stroke} d={['M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8', 'M2 17h20', 'M6 10V7a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v3']} />
);
const IconCal = ({ size = 12, stroke = HOTEL_A }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Rect x="3" y="4" width="18" height="18" rx="2" stroke={stroke} strokeWidth="2" fill="none" />
    <Path d="M16 2v4 M8 2v4 M3 10h18" stroke={stroke} strokeWidth="2" fill="none" strokeLinecap="round" />
  </Svg>
);
const IconCamaMini = ({ size = 12, stroke = HOTEL_A }) => (
  <Stroke size={size} stroke={stroke} d={['M2 18v-6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v6', 'M2 18h20', 'M6 10V8a2 2 0 0 1 2-2h3v4']} />
);
const PLANE_D = 'M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z';
const IconAvion = ({ size = 15, stroke = VUELO_A }) => (<Stroke size={size} stroke={stroke} d={PLANE_D} />);
// Llama (para el banner de OFERTA de cotización)
const IconFlama = ({ size = 14, stroke = '#8A6D00' }) => (
  <Stroke size={size} stroke={stroke} sw={2} d={'M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.07-2.14-.22-4.05 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.15.43-2.29 1-3a2.5 2.5 0 0 0 2.5 2.5z'} />
);
const IconAvionFill = ({ size = 12, color = VUELO_A }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z" fill={color} />
  </Svg>
);
// Encabezado con degradado (Svg de fondo + icono + nombre encima)
const CardHead = ({ gradId, a, b, icon, nombre }) => (
  <View style={{ position: 'relative', height: 26, justifyContent: 'center' }}>
    <Svg width={CONTENT_W} height={26} style={{ position: 'absolute', top: 0, left: 0 }}>
      <Defs><LinearGradient id={gradId} x1="0" y1="0" x2="1" y2="0"><Stop offset="0" stopColor={a} /><Stop offset="1" stopColor={b} /></LinearGradient></Defs>
      <Rect x="0" y="0" width={CONTENT_W} height="26" fill={`url(#${gradId})`} />
    </Svg>
    <View style={{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14 }}>
      {icon}
      <Text style={{ color: '#fff', fontFamily: 'DM Sans', fontWeight: 700, fontSize: 12, marginLeft: 9 }}>{nombre}</Text>
    </View>
  </View>
);

const styles = StyleSheet.create({
  page: {
    paddingTop: 40,
    paddingBottom: 40,
    paddingHorizontal: 40,
    fontFamily: 'DM Sans',
    color: COLORS.textoOscuro,
    fontSize: 10,
  },
  // Mini-footer de paginación (fixed en todas las páginas, dentro del margen inferior)
  pageFooter: {
    position: 'absolute',
    bottom: 16,
    left: 40,
    right: 40,
    textAlign: 'center',
    fontSize: 7.5,
    color: COLORS.textoClaro,
    fontFamily: 'DM Sans',
  },

  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 9,
  },
  logo: { width: 165, height: 38, objectFit: 'contain' },
  folioBox: {
    backgroundColor: COLORS.rosaSuave,
    borderRadius: 6,
    paddingVertical: 10,
    paddingHorizontal: 16,
    minWidth: 140,
    alignItems: 'flex-start',
  },
  folioLabel: {
    fontSize: 7,
    color: COLORS.textoMedio,
    letterSpacing: 1.5,
    fontFamily: 'DM Sans',
    fontWeight: 500,
    marginBottom: 3,
  },
  folioNumber: {
    fontSize: 24,
    color: COLORS.rosa,
    fontFamily: 'Space Grotesk',
    fontWeight: 700,
    marginBottom: 2,
    letterSpacing: -0.5,
  },
  folioDate: { fontSize: 9, color: COLORS.textoMedio, fontFamily: 'DM Sans' },

  divisor: { height: 1.5, backgroundColor: COLORS.rosa, marginVertical: 8 },

  sectionTitle: {
    fontSize: 10,
    color: COLORS.rosa,
    fontFamily: 'DM Sans',
    fontWeight: 700,
    letterSpacing: 2.5,
    marginBottom: 7,
    marginTop: 0,
  },

  clientGrid: { flexDirection: 'row', flexWrap: 'wrap' },
  clientItem: {
    width: '50%',
    paddingVertical: 4,
    paddingLeft: 10,
    borderLeftWidth: 2,
    borderLeftColor: COLORS.rosa,
    marginBottom: 6,
  },
  clientLabel: {
    fontSize: 7,
    color: COLORS.textoClaro,
    letterSpacing: 1,
    marginBottom: 3,
    fontFamily: 'DM Sans',
    fontWeight: 500,
  },
  clientValue: {
    fontSize: 11,
    color: COLORS.textoOscuro,
    fontFamily: 'DM Sans',
    fontWeight: 700,
  },

  tableHeader: {
    flexDirection: 'row',
    backgroundColor: COLORS.rosaSuave,
    paddingVertical: 7,
    paddingHorizontal: 12,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    borderBottomWidth: 2,
    borderBottomColor: COLORS.rosa,
  },
  tableHeaderCell: {
    fontSize: 8,
    color: COLORS.rosa,
    fontFamily: 'DM Sans',
    fontWeight: 700,
    letterSpacing: 1.2,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 7,
    paddingHorizontal: 12,
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS.bordeClaro,
  },
  colServicio: { width: '55%' },
  colDestino:  { width: '25%' },
  colFecha:    { width: '20%' },

  servicioNombre: {
    fontSize: 10.5,
    color: COLORS.textoOscuro,
    fontFamily: 'DM Sans',
    fontWeight: 700,
    marginBottom: 2,
  },
  servicioSku: { fontSize: 8, color: COLORS.textoClaro, fontFamily: 'DM Sans' },
  tableCell: { fontSize: 9.5, color: COLORS.textoMedio, fontFamily: 'DM Sans' },

  // ─── Resumen de Pago: gradiente PNG de fondo ─────────────────────────
  // La altura se calcula dinámicamente según número de pagos en historial
  resumenWrapper: {
    marginTop: 11,
    borderRadius: 10,
    overflow: 'hidden',
    position: 'relative',
  },
  resumenBg: { width: '100%' },
  resumenInner: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    padding: 13,
  },
  resumenTitle: {
    fontSize: 10,
    color: COLORS.blanco,
    letterSpacing: 2.5,
    fontFamily: 'DM Sans',
    fontWeight: 700,
    marginBottom: 8,
  },
  resumenRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 3,
  },
  resumenLabel: { fontSize: 12, color: COLORS.blanco, fontFamily: 'DM Sans', fontWeight: 400 },
  resumenValue: { fontSize: 12, color: COLORS.blanco, fontFamily: 'DM Sans', fontWeight: 500 },
  resumenDivider: { height: 1, backgroundColor: 'rgba(255,255,255,0.45)', marginVertical: 8 },
  resumenSaldoLabel: {
    fontSize: 15,
    color: COLORS.blanco,
    fontFamily: 'DM Sans',
    fontWeight: 700,
  },
  resumenSaldoValue: {
    fontSize: 19,
    color: COLORS.blanco,
    fontFamily: 'Space Grotesk',
    fontWeight: 700,
  },

  // ─── Para ajuste: línea con etiqueta + fecha (entre paréntesis) + monto ─
  ajusteRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 5,
  },
  ajusteEtiqueta: {
    fontSize: 12,
    color: COLORS.blanco,
    fontFamily: 'DM Sans',
    fontWeight: 400,
  },
  ajusteFecha: {
    fontSize: 9,
    color: 'rgba(255,255,255,0.85)',
    fontFamily: 'DM Sans',
    fontWeight: 400,
    marginLeft: 8,
  },
  ajusteMonto: {
    fontSize: 12,
    color: COLORS.blanco,
    fontFamily: 'DM Sans',
    fontWeight: 500,
  },

  infoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 9,
    marginHorizontal: -4,
  },
  infoCell: { width: '50%', paddingHorizontal: 4, paddingVertical: 4 },
  infoCellFull: { width: '100%', paddingHorizontal: 4, paddingVertical: 4 },
  infoCellInner: {
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderLeftWidth: 3,
    borderRadius: 4,
    backgroundColor: COLORS.fondoFila,
  },
  infoLabel: {
    fontSize: 7,
    color: COLORS.textoClaro,
    letterSpacing: 1.2,
    marginBottom: 4,
    fontFamily: 'DM Sans',
    fontWeight: 500,
  },
  infoValue: {
    fontSize: 10.5,
    color: COLORS.textoOscuro,
    fontFamily: 'DM Sans',
    fontWeight: 700,
  },

  repBox: {
    marginTop: 16,
    padding: 11,
    borderWidth: 1,
    borderColor: COLORS.bordeClaro,
    borderRadius: 6,
  },
  repTitle: {
    fontSize: 9,
    color: COLORS.rosa,
    fontFamily: 'DM Sans',
    fontWeight: 700,
    letterSpacing: 2.5,
    marginBottom: 10,
  },
  repGrid: { flexDirection: 'row' },
  repItem: {
    width: '50%',
    borderLeftWidth: 2,
    borderLeftColor: COLORS.rosa,
    paddingLeft: 10,
  },

  logoCenter: {
    width: 130,
    height: 30,
    objectFit: 'contain',
    alignSelf: 'center',
    marginTop: 12,
    marginBottom: 6,
  },

  page2Center: { alignItems: 'center', marginBottom: 14 },
  logoFooter: { width: 130, height: 30, marginBottom: 10, objectFit: 'contain' },
  gracias: {
    fontSize: 11,
    color: COLORS.textoMedio,
    marginBottom: 9,
    fontFamily: 'DM Sans',
  },
  empresaInfo: {
    fontSize: 9.5,
    color: COLORS.textoMedio,
    textAlign: 'center',
    lineHeight: 1.6,
    marginBottom: 4,
    fontFamily: 'DM Sans',
  },
  empresaInfoBold: {
    fontSize: 9.5,
    color: COLORS.rosa,
    fontFamily: 'DM Sans',
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: 2,
  },
  empresaMuted: {
    fontSize: 9,
    color: COLORS.textoClaro,
    textAlign: 'center',
    marginTop: 8,
    fontFamily: 'DM Sans',
  },

  terminosBox: {
    marginTop: 12,
    padding: 13,
    backgroundColor: COLORS.rosaSuave,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.rosaSuaveBorde,
  },
  terminosTitle: {
    fontSize: 11,
    color: COLORS.textoOscuro,
    fontFamily: 'DM Sans',
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: 10,
  },
  terminoItem: {
    fontSize: 9,
    color: COLORS.textoMedio,
    textAlign: 'center',
    marginBottom: 5,
    lineHeight: 1.4,
    fontFamily: 'DM Sans',
  },

  cotizBox: {
    marginTop: 12,
    padding: 13,
    backgroundColor: '#fffaeb',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#fde68a',
  },
  cotizTitle: {
    fontSize: 11,
    color: '#92400e',
    fontFamily: 'DM Sans',
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: 10,
  },
  cotizItem: {
    fontSize: 9,
    color: '#78350f',
    textAlign: 'center',
    marginBottom: 5,
    lineHeight: 1.4,
    fontFamily: 'DM Sans',
  },
});

// Contabilidad v3 (Bloque 50): textos fijos del cupón del cliente en es / en / pt. Montos y moneda no cambian.
const TX = {
  es: { gracias: '¡Gracias por confiar en nosotros!', telOficina: 'Tel Oficina', duda: 'Para cualquier duda, contacta a tu representante de ventas.', cotizTitulo: '⚠ COTIZACIÓN — VIGENCIA 24 HORAS', cotiz: ['• Vigencia de 24 horas a partir de su emisión.', '• Precios sujetos a cambio sin previo aviso.', '• No se garantiza disponibilidad sin depósito confirmado.', '• Tipos de cambio referenciales.', '• Para confirmar, contacte a su representante.'], terminos: 'Términos y Condiciones:', t: ['• El depósito es NO REEMBOLSABLE en caso de cancelación por parte del Pasajero.', '• El cliente es responsable de proporcionar información correcta y completa.', '• Paquetes de Vuelo y Hotel no son cancelables después de emitida y confirmada la reserva.', '• En Tours puede aplicar penalidad. Info: cancun100.com/politica-de-cancelacion', '• Cancun100 se reserva el derecho de modificar itinerarios por fuerza mayor.'], infoCliente: 'INFORMACIÓN DEL CLIENTE', nombreCompleto: 'NOMBRE COMPLETO', hotelPunto: 'HOTEL / PUNTO DE ENCUENTRO', habitacion: 'NÚMERO DE HABITACIÓN', composicion: 'COMPOSICIÓN DEL GRUPO', whatsappTel: 'WHATSAPP / TELÉFONO', telefono: 'TELÉFONO', email: 'EMAIL', servicios: 'SERVICIOS CONTRATADOS', servicio: 'SERVICIO / ACTIVIDAD', destino: 'DESTINO', fecha: 'FECHA', hospedaje: 'TU HOSPEDAJE', hospedajeNota: 'Detalles del hospedaje se confirman por tu representante.', vuelo: 'TU VUELO', aerolinea: 'Aerolínea', salida: 'Salida', regreso: 'Regreso', vueloNota: '* Los detalles del vuelo se confirman después de la compra.', estadoCuenta: 'ESTADO DE CUENTA', resumenPago: 'RESUMEN DE PAGO', total: 'Total', deposito: 'Depósito', saldoPagar: 'Saldo a Pagar', saldoActual: 'Saldo Actual', metodoPago: 'MÉTODO DE PAGO', notas: 'NOTAS ADICIONALES', importante: 'IMPORTANTE', lineaContacto: 'LÍNEA DE CONTACTO', pagoBalance: 'PAGO DE BALANCE', representante: 'REPRESENTANTE DE VENTAS', nombre: 'NOMBRE', pagina: 'Página', de: 'de', cotizacion: 'COTIZACIÓN', cuponAjuste: 'CUPÓN DE AJUSTE', folioReserva: 'FOLIO DE RESERVA', checkin: 'Check-in', checkout: 'Check-out', habitaciones: 'Habitaciones', plan: 'Plan' },
  en: { gracias: 'Thank you for trusting us!', telOficina: 'Office', duda: 'For any questions, contact your sales representative.', cotizTitulo: '⚠ QUOTE — VALID FOR 24 HOURS', cotiz: ['• Valid for 24 hours from issue.', '• Prices subject to change without notice.', '• Availability is not guaranteed without a confirmed deposit.', '• Exchange rates are for reference only.', '• To confirm, contact your representative.'], terminos: 'Terms and Conditions:', t: ['• The deposit is NON-REFUNDABLE if the passenger cancels.', '• The customer is responsible for providing correct and complete information.', '• Flight and Hotel packages cannot be cancelled once the booking is issued and confirmed.', '• Tours may carry a penalty. Info: cancun100.com/politica-de-cancelacion', '• Cancun100 reserves the right to modify itineraries due to force majeure.'], infoCliente: 'CUSTOMER INFORMATION', nombreCompleto: 'FULL NAME', hotelPunto: 'HOTEL / MEETING POINT', habitacion: 'ROOM NUMBER', composicion: 'GROUP', whatsappTel: 'WHATSAPP / PHONE', telefono: 'PHONE', email: 'EMAIL', servicios: 'BOOKED SERVICES', servicio: 'SERVICE / ACTIVITY', destino: 'PICKUP', fecha: 'DATE', hospedaje: 'YOUR STAY', hospedajeNota: 'Accommodation details will be confirmed by your representative.', vuelo: 'YOUR FLIGHT', aerolinea: 'Airline', salida: 'Departure', regreso: 'Return', vueloNota: '* Flight details are confirmed after purchase.', estadoCuenta: 'ACCOUNT STATEMENT', resumenPago: 'PAYMENT SUMMARY', total: 'Total', deposito: 'Deposit', saldoPagar: 'Balance Due', saldoActual: 'Current Balance', metodoPago: 'PAYMENT METHOD', notas: 'ADDITIONAL NOTES', importante: 'IMPORTANT', lineaContacto: 'CONTACT LINE', pagoBalance: 'BALANCE PAYMENT', representante: 'SALES REPRESENTATIVE', nombre: 'NAME', pagina: 'Page', de: 'of', cotizacion: 'QUOTE', cuponAjuste: 'ADJUSTMENT VOUCHER', folioReserva: 'BOOKING NUMBER', checkin: 'Check-in', checkout: 'Check-out', habitaciones: 'Rooms', plan: 'Plan' },
  pt: { gracias: 'Obrigado por confiar em nós!', telOficina: 'Tel Escritório', duda: 'Para qualquer dúvida, fale com o seu representante de vendas.', cotizTitulo: '⚠ COTAÇÃO — VALIDADE 24 HORAS', cotiz: ['• Validade de 24 horas a partir da emissão.', '• Preços sujeitos a alteração sem aviso prévio.', '• Não se garante disponibilidade sem depósito confirmado.', '• Taxas de câmbio referenciais.', '• Para confirmar, fale com o seu representante.'], terminos: 'Termos e Condições:', t: ['• O depósito NÃO É REEMBOLSÁVEL em caso de cancelamento pelo passageiro.', '• O cliente é responsável por fornecer informações corretas e completas.', '• Pacotes de Voo e Hotel não podem ser cancelados depois de emitida e confirmada a reserva.', '• Em tours pode haver penalidade. Info: cancun100.com/politica-de-cancelacion', '• A Cancun100 reserva-se o direito de alterar itinerários por força maior.'], infoCliente: 'INFORMAÇÕES DO CLIENTE', nombreCompleto: 'NOME COMPLETO', hotelPunto: 'HOTEL / PONTO DE ENCONTRO', habitacion: 'NÚMERO DO QUARTO', composicion: 'COMPOSIÇÃO DO GRUPO', whatsappTel: 'WHATSAPP / TELEFONE', telefono: 'TELEFONE', email: 'EMAIL', servicios: 'SERVIÇOS CONTRATADOS', servicio: 'SERVIÇO / ATIVIDADE', destino: 'DESTINO', fecha: 'DATA', hospedaje: 'SUA HOSPEDAGEM', hospedajeNota: 'Os detalhes da hospedagem são confirmados pelo seu representante.', vuelo: 'SEU VOO', aerolinea: 'Companhia', salida: 'Ida', regreso: 'Volta', vueloNota: '* Os detalhes do voo são confirmados após a compra.', estadoCuenta: 'EXTRATO', resumenPago: 'RESUMO DO PAGAMENTO', total: 'Total', deposito: 'Depósito', saldoPagar: 'Saldo a Pagar', saldoActual: 'Saldo Atual', metodoPago: 'FORMA DE PAGAMENTO', notas: 'OBSERVAÇÕES', importante: 'IMPORTANTE', lineaContacto: 'LINHA DE CONTATO', pagoBalance: 'PAGAMENTO DO SALDO', representante: 'REPRESENTANTE DE VENDAS', nombre: 'NOME', pagina: 'Página', de: 'de', cotizacion: 'COTAÇÃO', cuponAjuste: 'CUPOM DE AJUSTE', folioReserva: 'NÚMERO DA RESERVA', checkin: 'Check-in', checkout: 'Check-out', habitaciones: 'Quartos', plan: 'Plano' },
};
const PDFClienteAnterior = ({ data, tipo = 'reserva' }) => {
  const L = TX[data?.idioma] || TX.es; // Bloque 50
  const moneda = data?.resumen?.moneda || 'MXN';
  const tieneContacto = data?.cliente?.telefono || data?.cliente?.email;
  const esCotizacion = tipo === 'cotizacion';
  const esAjuste = tipo === 'ajuste';
  const historial = data?.resumen?.historial || [];
  
  // FASE 1 RECUPERADO: multi-moneda en el resumen (solo reserva/cotización, no ajuste)
  const bloquesMoneda = (!esAjuste && Array.isArray(data?.resumen?.bloques_moneda) && data.resumen.bloques_moneda.length > 0)
    ? data.resumen.bloques_moneda
    : null;
  const multimoneda = bloquesMoneda && bloquesMoneda.length > 1;
  const tasaUsdPdf = data?.resumen?.tasa_usd_pdf;
  const tasaEurPdf = data?.resumen?.tasa_eur_pdf;
  // Símbolo y formato según moneda
  const fmtPorMoneda = (amt, mon) => {
    const sym = mon === 'EUR' ? '\u20ac' : '$';
    if (mon === 'EUR') {
      return sym + (amt || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    return sym + Math.round(amt || 0).toLocaleString('es-MX');
  };

  // Label de la cajita de folio según tipo
  const folioLabel = esCotizacion ? L.cotizacion
                   : esAjuste     ? L.cuponAjuste
                   : L.folioReserva;

  // Altura dinámica del bloque Resumen
  // - Reserva/Cotización (1 moneda): 145
  // - Reserva/Cotización (N monedas): 145 + 75 × (N-1)   ← TC ya no se muestra en PDF cliente
  // - Ajuste: base 110 + 24 por cada línea de historial
  const extraMonedasAjuste = (esAjuste && Array.isArray(data?.resumen?.bloques_moneda))
    ? data.resumen.bloques_moneda.filter((b) => b.moneda !== 'MXN').length : 0;
  // Densidad Plan A: alturas del resumen más compactas (paddings/rows reducidos).
  let resumenHeight;
  if (esAjuste) {
    resumenHeight = 98 + (historial.length * 21) + (extraMonedasAjuste * 18);
  } else if (multimoneda) {
    resumenHeight = 128 + 66 * (bloquesMoneda.length - 1);
  } else {
    resumenHeight = 128;
  }

  // ── Identidad AMARILLA de la COTIZACIÓN (paleta aprobada). Solo aplica si esCotizacion;
  //    el PDF de reserva/ajuste queda intacto en su rosa→naranja. Café miel para legibilidad. ──
  const YEL = { grad1: '#FFD43B', grad2: '#FFB300', pale: '#FFF8D6', ink1: '#E6A800', ink2: '#C79100', ink3: '#8A6D00' };
  const arr = (...xs) => xs.filter(Boolean); // arma arrays de estilo limpiando falsy
  const co = (ov) => esCotizacion ? ov : null; // override solo en cotización
  // Estilos reutilizados con override amarillo
  const sSection = arr(styles.sectionTitle, co({ color: YEL.ink1 }));
  const sClientItem = arr(styles.clientItem, co({ borderLeftColor: YEL.ink1 }));
  const sTableHeader = arr(styles.tableHeader, co({ backgroundColor: YEL.pale, borderBottomColor: YEL.ink1 }));
  const sRepItem = arr(styles.repItem, co({ borderLeftColor: YEL.ink1 }));
  const thc = (colStyle) => arr(styles.tableHeaderCell, colStyle, co({ color: YEL.ink3 }));
  // Resumen: sobre amarillo el texto blanco es ilegible → café miel oscuro en cotización.
  const rTitle = arr(styles.resumenTitle, co({ color: '#8A6D00' }));
  const rLabel = arr(styles.resumenLabel, co({ color: '#8A6D00' }));
  const rValue = arr(styles.resumenValue, co({ color: '#6b5200' }));
  const rDiv = arr(styles.resumenDivider, co({ backgroundColor: 'rgba(138,109,0,0.30)' }));
  const rSaldoL = arr(styles.resumenSaldoLabel, co({ color: '#8A6D00' }));
  const rSaldoV = arr(styles.resumenSaldoValue, co({ color: '#5c4600' }));

  // Contenido del footer institucional (gracias + contacto + T&C/vigencia). Reutilizado por
  // ambos caminos de anclaje (reserva: después del contenedor; cotización: dentro con marginTop:auto).
  const footerInner = (
    <>
      <View style={styles.page2Center}>
        <Image src={LOGO_BASE64} style={styles.logoFooter} />
        <Text style={styles.gracias}>{L.gracias}</Text>
        <Text style={styles.empresaInfoBold}>
          cancun100.com{' '}
          <Text style={{ color: COLORS.textoMedio, fontFamily: 'DM Sans', fontWeight: 400 }}>by Xpectative Travel</Text>
        </Text>
        <Text style={styles.empresaInfo}>{DIRECCION}</Text>
        <Text style={styles.empresaInfo}>
          {L.telOficina}: <Text style={{ fontFamily: 'DM Sans', fontWeight: 700 }}>{(data?.contacto || contactoPorCanal('sala')).telefono}</Text>
          {' '}|  {(data?.contacto || contactoPorCanal('sala')).etiquetaWhatsapp}: <Text style={{ fontFamily: 'DM Sans', fontWeight: 700 }}>{(data?.contacto || contactoPorCanal('sala')).whatsapp}</Text>
        </Text>
        <Text style={styles.empresaInfo}>{(data?.contacto || contactoPorCanal('sala')).email}</Text>
        <Text style={styles.empresaInfo}>@cancun100.site — Facebook | Instagram | TikTok</Text>
        <Text style={styles.empresaMuted}>{L.duda}</Text>
      </View>
      {esCotizacion ? (
        <View style={styles.cotizBox}>
          <Text style={styles.cotizTitle}>{L.cotizTitulo}</Text>
          {L.cotiz.map((t, i) => <Text key={i} style={styles.cotizItem}>{t}</Text>)}
        </View>
      ) : (
        <View style={styles.terminosBox}>
          <Text style={styles.terminosTitle}>{L.terminos}</Text>
          {L.t.map((t, i) => <Text key={i} style={styles.terminoItem}>{t}</Text>)}
        </View>
      )}
    </>
  );

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        {/* Mini-footer de paginación: fijo al pie de TODAS las páginas. */}
        <Text style={styles.pageFooter} fixed render={({ pageNumber, totalPages }) => `cancun100.com · ${L.pagina} ${pageNumber} ${L.de} ${totalPages}`} />
        {/* flexGrow: contenedor que llena la página. Reserva: el footer va DESPUÉS (anclado por el
            crecimiento). Cotización: el footer va DENTRO con marginTop:'auto' → flexbox lo empuja al
            fondo de la ÚLTIMA página, pegado e independiente de dónde caigan los saltos. */}
        <View style={{ flexGrow: 1 }}>
        <View style={styles.header}>
          <Image src={LOGO_BASE64} style={styles.logo} />
          <View style={arr(styles.folioBox, co({ backgroundColor: YEL.pale }))}>
            <Text style={styles.folioLabel}>{folioLabel}</Text>
            <Text style={arr(styles.folioNumber, co({ color: YEL.ink1 }))}>{data?.folio || '—'}</Text>
            <Text style={styles.folioDate}>{data?.fecha_creacion || ''}</Text>
          </View>
        </View>

        <View style={arr(styles.divisor, co({ backgroundColor: YEL.ink1 }))} />

        <Text style={sSection}>{L.infoCliente}</Text>
        <View style={styles.clientGrid}>
          <View style={sClientItem}>
            <Text style={styles.clientLabel}>{L.nombreCompleto}</Text>
            <Text style={styles.clientValue}>{data?.cliente?.nombre || '—'}</Text>
          </View>
          {/* APP 2/10: fuera hotel y habitación de la cabecera (el pickup va por tour); se queda el ID de Komito si existe */}
          {data?.cliente?.komito ? (
            <View style={sClientItem}>
              <Text style={styles.clientLabel}>ID KOMITO</Text>
              <Text style={styles.clientValue}>{String(data.cliente.komito)}</Text>
            </View>
          ) : null}
          <View style={sClientItem}>
            <Text style={styles.clientLabel}>{L.composicion}</Text>
            <Text style={styles.clientValue}>
              {data?.cliente?.adultos || 0} Adultos, {data?.cliente?.menores || 0} Menores, {data?.cliente?.infantes || 0} Infantes
            </Text>
          </View>
          {tieneContacto && data?.cliente?.telefono && (
            <View style={sClientItem}>
              <Text style={styles.clientLabel}>{esCotizacion ? L.whatsappTel : L.telefono}</Text>
              <Text style={styles.clientValue}>{data.cliente.telefono}</Text>
            </View>
          )}
          {tieneContacto && data?.cliente?.email && (
            <View style={sClientItem}>
              <Text style={styles.clientLabel}>{L.email}</Text>
              <Text style={styles.clientValue}>{data.cliente.email}</Text>
            </View>
          )}
        </View>

        <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 10, marginBottom: 12 }}>
          <IconMaleta stroke={esCotizacion ? YEL.ink1 : COLORS.rosa} />
          <Text style={arr(styles.sectionTitle, { marginTop: 0, marginBottom: 0, marginLeft: 7 }, co({ color: YEL.ink1 }))}>{L.servicios}</Text>
        </View>
        <View style={sTableHeader}>
          <Text style={thc(styles.colServicio)}>{L.servicio}</Text>
          <Text style={thc(styles.colDestino)}>{L.destino}</Text>
          <Text style={thc(styles.colFecha)}>{L.fecha}</Text>
        </View>
        {(data?.servicios || []).map((s, i) => (
          <View key={i} style={styles.tableRow} wrap={false}>
            <View style={styles.colServicio}>
              <Text style={styles.servicioNombre}>{s.nombre}</Text>
              <Text style={styles.servicioSku}>{s.tipo ? `${s.tipo} · ` : ''}{s.sku} · {s.proveedor_nombre}</Text>
            </View>
            <Text style={[styles.tableCell, styles.colDestino]}>{s.destino || '—'}</Text>
            <Text style={[styles.tableCell, styles.colFecha]}>{s.fecha || '—'}</Text>
          </View>
        ))}

        {/* ─── TU HOSPEDAJE (solo si hay customs tipo hotel) ─── */}
        {Array.isArray(data?.hospedaje) && data.hospedaje.map((h, i) => {
          const items = [];
          if (h.checkin) items.push({ ic: <IconCal />, label: L.checkin, val: h.checkin });
          if (h.checkout) items.push({ ic: <IconCal />, label: L.checkout, val: h.checkout });
          if (h.habitaciones) items.push({ ic: <IconCamaMini />, label: L.habitaciones, val: h.habitaciones });
          if (h.plan) items.push({ ic: null, label: L.plan, val: h.plan });
          return (
            <View key={`h${i}`} wrap={false}>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 10, marginBottom: 6 }}>
                <IconHotelEdif />
                <Text style={[styles.sectionTitle, { color: HOTEL_A, marginTop: 0, marginBottom: 0, marginLeft: 7 }]}>{L.hospedaje}</Text>
              </View>
              <View style={{ borderRadius: 10, overflow: 'hidden', borderWidth: 1, borderColor: '#ececf4' }}>
                <CardHead gradId={`gh${i}`} a={HOTEL_A} b={HOTEL_B} icon={<IconCama />} nombre={h.nombre} />
                <View style={{ padding: 10, flexDirection: 'row', flexWrap: 'wrap', backgroundColor: '#fff' }}>
                  {items.length === 0 && <Text style={{ fontSize: 10.5, color: '#8a8a99', fontFamily: 'DM Sans' }}>{L.hospedajeNota}</Text>}
                  {items.map((it, j) => (
                    <View key={j} style={{ flexDirection: 'row', alignItems: 'center', width: '50%', paddingVertical: 3 }}>
                      {it.ic}
                      <Text style={{ fontSize: 11, color: '#555', fontFamily: 'DM Sans', marginLeft: it.ic ? 6 : 0 }}>{it.label}: <Text style={{ color: COLORS.textoOscuro, fontWeight: 700 }}>{it.val}</Text></Text>
                    </View>
                  ))}
                </View>
              </View>
            </View>
          );
        })}

        {/* ─── TU VUELO (solo si hay customs tipo vuelo) ─── */}
        {Array.isArray(data?.vuelos) && data.vuelos.map((v, i) => {
          const hayRuta = v.origen && v.destino;
          const faltan = !(v.origen && v.destino && v.salida && v.regreso);
          return (
            <View key={`v${i}`} wrap={false}>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 10, marginBottom: 6 }}>
                <IconAvion size={14} />
                <Text style={[styles.sectionTitle, { color: VUELO_A, marginTop: 0, marginBottom: 0, marginLeft: 7 }]}>{L.vuelo}</Text>
              </View>
              <View style={{ borderRadius: 10, overflow: 'hidden', borderWidth: 1, borderColor: '#ececf4' }}>
                <CardHead gradId={`gv${i}`} a={VUELO_A} b={VUELO_B} icon={<IconAvion size={15} stroke="#fff" />} nombre={v.nombre} />
                <View style={{ padding: 10, backgroundColor: '#fff' }}>
                  {hayRuta && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', width: '100%', marginBottom: 8 }}>
                      <Text style={{ fontSize: 14, fontWeight: 700, color: COLORS.textoOscuro, fontFamily: 'Space Grotesk' }}>{v.origen}</Text>
                      <View style={{ flex: 1, marginHorizontal: 8, height: 1, borderBottomWidth: 1, borderBottomColor: VUELO_A, borderStyle: 'dashed' }} />
                      <IconAvionFill />
                      <View style={{ flex: 1, marginHorizontal: 8, height: 1, borderBottomWidth: 1, borderBottomColor: VUELO_A, borderStyle: 'dashed' }} />
                      <Text style={{ fontSize: 14, fontWeight: 700, color: COLORS.textoOscuro, fontFamily: 'Space Grotesk' }}>{v.destino}</Text>
                    </View>
                  )}
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                    {v.aerolinea ? <View style={{ width: '100%', paddingVertical: 2 }}><Text style={{ fontSize: 11, color: '#555', fontFamily: 'DM Sans' }}>{L.aerolinea}: <Text style={{ color: COLORS.textoOscuro, fontWeight: 700 }}>{v.aerolinea}</Text></Text></View> : null}
                    {v.salida ? <View style={{ width: '50%', paddingVertical: 2 }}><Text style={{ fontSize: 11, color: '#555', fontFamily: 'DM Sans' }}>{L.salida}: <Text style={{ color: COLORS.textoOscuro, fontWeight: 700 }}>{v.salida}</Text></Text></View> : null}
                    {v.regreso ? <View style={{ width: '50%', paddingVertical: 2 }}><Text style={{ fontSize: 11, color: '#555', fontFamily: 'DM Sans' }}>{L.regreso}: <Text style={{ color: COLORS.textoOscuro, fontWeight: 700 }}>{v.regreso}</Text></Text></View> : null}
                  </View>
                  {faltan && <Text style={{ fontSize: 9, color: '#9aa', fontFamily: 'DM Sans', marginTop: 4 }}>{L.vueloNota}</Text>}
                </View>
              </View>
            </View>
          );
        })}

        {/* ─── Bloque de OFERTA (solo cotización con precio_regular_oferta) ─── */}
        {esCotizacion && data?.oferta && (
          <View style={{ marginTop: 11, borderRadius: 10, overflow: 'hidden' }} wrap={false}>
            {/* Banner degradado con llama */}
            <View style={{ position: 'relative', height: 30, justifyContent: 'center' }}>
              <Svg width={CONTENT_W} height={30} style={{ position: 'absolute', top: 0, left: 0 }}>
                <Defs><LinearGradient id="ofergrad" x1="0" y1="0" x2="1" y2="0"><Stop offset="0" stopColor="#FFE14D" /><Stop offset="1" stopColor="#FFC107" /></LinearGradient></Defs>
                <Rect x="0" y="0" width={CONTENT_W} height="30" fill="url(#ofergrad)" />
              </Svg>
              <View style={{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14 }}>
                <IconFlama stroke="#8A6D00" />
                <Text style={{ marginLeft: 8, color: '#8A6D00', fontFamily: 'DM Sans', fontWeight: 700, fontSize: 11 }}>
                  Oferta especial — de <Text style={{ textDecoration: 'line-through' }}>${fmtMXN(data.oferta.precio_regular)}</Text> a solo
                </Text>
              </View>
            </View>
            {/* Cuerpo crema: precio final grande + badge de ahorro */}
            <View style={{ backgroundColor: '#FFFDF0', paddingVertical: 12, paddingHorizontal: 14, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderWidth: 1, borderTopWidth: 0, borderColor: '#FFE9A8', borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }}>
              <Text style={{ fontFamily: 'Space Grotesk', fontWeight: 700, fontSize: 26, color: '#E6A800' }}>${fmtMXN(data.oferta.precio_final)}</Text>
              <View style={{ backgroundColor: '#FFF3BF', borderRadius: 20, paddingVertical: 6, paddingHorizontal: 12 }}>
                <Text style={{ fontFamily: 'DM Sans', fontWeight: 700, fontSize: 11, color: '#8A6D00' }}>Ahorras ${fmtMXN(data.oferta.ahorro)} · {data.oferta.porcentaje}%</Text>
              </View>
            </View>
          </View>
        )}

        {/* APP 2/28 (RP-10): Importante — nota de venta de cada producto (solo reserva, nunca cotización), sin emoji */}
        {!esCotizacion && Array.isArray(data?.importante) && data.importante.length > 0 && (
          <View style={{ marginTop: 10, marginBottom: 2, paddingVertical: 8, paddingHorizontal: 10, borderLeftWidth: 3, borderLeftColor: COLORS.acentoNaranja, borderRadius: 4, backgroundColor: COLORS.fondoFila }} wrap={false}>
            <Text style={styles.infoLabel}>{L.importante}</Text>
            {data.importante.map((x, i) => (
              <Text key={i} style={{ fontSize: 9.5, color: COLORS.textoOscuro, fontFamily: 'DM Sans', marginBottom: 2, lineHeight: 1.35 }}>
                {x.nombre ? <Text style={{ fontWeight: 700 }}>{x.nombre}: </Text> : null}{x.nota}
              </Text>
            ))}
          </View>
        )}

        {/* ─── Bloque Resumen de Pago ─── */}
        <View style={styles.resumenWrapper} wrap={false}>
          {esCotizacion ? (
            <Svg width={CONTENT_W} height={resumenHeight} style={styles.resumenBg}>
              <Defs><LinearGradient id="resgrad" x1="0" y1="0" x2="1" y2="0"><Stop offset="0" stopColor={YEL.grad1} /><Stop offset="1" stopColor={YEL.grad2} /></LinearGradient></Defs>
              <Rect x="0" y="0" width={CONTENT_W} height={resumenHeight} fill="url(#resgrad)" />
            </Svg>
          ) : (
            <Image src={gradientPng} style={[styles.resumenBg, { height: resumenHeight }]} />
          )}
          <View style={styles.resumenInner}>
            <Text style={rTitle}>
              {esAjuste ? L.estadoCuenta : L.resumenPago}
            </Text>

            {esAjuste ? (
              <>
                {/* Total siempre arriba */}
                <View style={styles.resumenRow}>
                  <Text style={styles.resumenLabel}>{L.total}</Text>
                  <Text style={styles.resumenValue}>{moneda} ${fmtMXN(data?.resumen?.total)}</Text>
                </View>
                {/* Línea divisora arriba del histórico */}
                <View style={styles.resumenDivider} />
                {/* Histórico de pagos: etiqueta · fecha (todo en una sola línea) */}
                {historial.map((h, i) => (
                  <View key={i} style={styles.ajusteRow}>
                    <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                      <Text style={styles.ajusteEtiqueta}>{h.etiqueta}</Text>
                      {h.fecha && <Text style={styles.ajusteFecha}>· {h.fecha}</Text>}
                    </View>
                    <Text style={styles.ajusteMonto}>{moneda} ${fmtMXN(h.monto)}</Text>
                  </View>
                ))}
                <View style={styles.resumenDivider} />
                <View style={styles.resumenRow}>
                  <Text style={styles.resumenSaldoLabel}>{L.saldoActual}</Text>
                  <Text style={styles.resumenSaldoValue}>{moneda} ${fmtMXN(data?.resumen?.saldo)}</Text>
                </View>
                {/* A2.3f — Equivalentes del saldo en otras monedas (si moneda_pdf trae USD/EUR) */}
                {Array.isArray(data?.resumen?.bloques_moneda) && data.resumen.bloques_moneda.filter((b) => b.moneda !== 'MXN').map((b) => (
                  <View key={b.moneda} style={styles.resumenRow}>
                    <Text style={styles.resumenLabel}>Saldo ({b.moneda})</Text>
                    <Text style={styles.resumenValue}>{fmtPorMoneda(b.saldo, b.moneda)}</Text>
                  </View>
                ))}
              </>
            ) : multimoneda ? (
              <>
                {/* FASE 1 RECUPERADO: multi-moneda — un bloque por cada moneda seleccionada */}
                {bloquesMoneda.map((b, i) => (
                  <View key={b.moneda}>
                    {i > 0 && <View style={rDiv} />}
                    <View style={styles.resumenRow}>
                      <Text style={rLabel}>{L.total} ({b.moneda})</Text>
                      <Text style={rValue}>{b.moneda} {fmtPorMoneda(b.total, b.moneda)}</Text>
                    </View>
                    <View style={styles.resumenRow}>
                      <Text style={rLabel}>{L.deposito} ({b.moneda})</Text>
                      <Text style={rValue}>{b.moneda} {fmtPorMoneda(b.deposito, b.moneda)}</Text>
                    </View>
                    <View style={styles.resumenRow}>
                      <Text style={rSaldoL}>{L.saldoPagar} ({b.moneda})</Text>
                      <Text style={rSaldoV}>{b.moneda} {fmtPorMoneda(b.saldo, b.moneda)}</Text>
                    </View>
                  </View>
                ))}
                {/* TC NO se muestra en PDF del cliente (es uso interno). Se muestra en PDF Comisión. */}
              </>
            ) : (
              <>
                {/* Una sola moneda — comportamiento clásico */}
                <View style={styles.resumenRow}>
                  <Text style={rLabel}>{L.total}</Text>
                  <Text style={rValue}>
                    {bloquesMoneda ? `${bloquesMoneda[0].moneda} ${fmtPorMoneda(bloquesMoneda[0].total, bloquesMoneda[0].moneda)}` : `${moneda} $${fmtMXN(data?.resumen?.total)}`}
                  </Text>
                </View>
                <View style={styles.resumenRow}>
                  <Text style={rLabel}>{L.deposito}</Text>
                  <Text style={rValue}>
                    {bloquesMoneda ? `${bloquesMoneda[0].moneda} ${fmtPorMoneda(bloquesMoneda[0].deposito, bloquesMoneda[0].moneda)}` : `${moneda} $${fmtMXN(data?.resumen?.deposito)}`}
                  </Text>
                </View>
                <View style={rDiv} />
                <View style={styles.resumenRow}>
                  <Text style={rSaldoL}>{L.saldoPagar}</Text>
                  <Text style={rSaldoV}>
                    {bloquesMoneda ? `${bloquesMoneda[0].moneda} ${fmtPorMoneda(bloquesMoneda[0].saldo, bloquesMoneda[0].moneda)}` : `${moneda} $${fmtMXN(data?.resumen?.saldo)}`}
                  </Text>
                </View>
                {/* TC NO se muestra en PDF del cliente (es uso interno). Se muestra en PDF Comisión. */}
              </>
            )}
          </View>
        </View>

        {/* Bloques de pago — Cotización solo muestra Método de Pago. Ajuste igual a reserva.
            BLINDAJE (solo cotización, para no tocar el PDF de reserva): el grid no se parte a
            media página (control de viudas). */}
        <View style={styles.infoGrid} wrap={esCotizacion ? false : undefined}>
          {esCotizacion ? (
            <>
              <View style={styles.infoCellFull}>
                <View style={[styles.infoCellInner, { borderLeftColor: YEL.ink1 }]}>
                  <Text style={styles.infoLabel}>{L.metodoPago}</Text>
                  <Text style={styles.infoValue}>{data?.pago?.metodo || '—'}</Text>
                </View>
              </View>
              <View style={styles.infoCellFull}>
                <View style={[styles.infoCellInner, { borderLeftColor: YEL.ink2 }]}>
                  <Text style={styles.infoLabel}>{L.notas}</Text>
                  <Text style={styles.infoValue}>{data?.pago?.notas || '—'}</Text>
                </View>
              </View>
            </>
          ) : (
            <>
              <View style={styles.infoCell}>
                <View style={[styles.infoCellInner, { borderLeftColor: COLORS.acentoRosa }]}>
                  <Text style={styles.infoLabel}>{L.metodoPago}</Text>
                  <Text style={styles.infoValue}>{data?.pago?.metodo || '—'}</Text>
                </View>
              </View>
              <View style={styles.infoCell}>
                <View style={[styles.infoCellInner, { borderLeftColor: COLORS.acentoNaranja }]}>
                  <Text style={styles.infoLabel}>{L.notas}</Text>
                  <Text style={styles.infoValue}>{data?.pago?.notas || '—'}</Text>
                </View>
              </View>
              <View style={styles.infoCell}>
                <View style={[styles.infoCellInner, { borderLeftColor: COLORS.acentoPurpura }]}>
                  <Text style={styles.infoLabel}>{L.lineaContacto}</Text>
                  <Text style={styles.infoValue}>{data?.pago?.linea_contacto || '—'}</Text>
                </View>
              </View>
              <View style={styles.infoCell}>
                <View style={[styles.infoCellInner, { borderLeftColor: COLORS.acentoAzul }]}>
                  <Text style={styles.infoLabel}>{L.pagoBalance}</Text>
                  <Text style={styles.infoValue}>{data?.pago?.pago_balance || '—'}</Text>
                </View>
              </View>
            </>
          )}
        </View>

        <View style={styles.repBox} wrap={false}>
          <Text style={arr(styles.repTitle, co({ color: YEL.ink1 }))}>{L.representante}</Text>
          <View style={styles.repGrid}>
            <View style={sRepItem}>
              <Text style={styles.clientLabel}>{L.nombre}</Text>
              <Text style={styles.clientValue}>{data?.representante?.nombre || '—'}</Text>
            </View>
            <View style={sRepItem}>
              <Text style={styles.clientLabel}>{L.telefono}</Text>
              <Text style={styles.clientValue}>{data?.representante?.telefono || '—'}</Text>
            </View>
          </View>
        </View>

        {/* (El logo centrado extra de cotización se removió: el footer institucional ya trae logo,
            evitando el duplicado.) */}
        {/* COTIZACIÓN: footer DENTRO del contenedor, empujado al fondo de la última página con
            marginTop:'auto' (pegado e independiente de dónde caiga el salto de página). wrap={false}
            mantiene el bloque (incluida la vigencia 24h) indivisible. */}
        {esCotizacion && (
          <View wrap={false} style={{ marginTop: 'auto', paddingTop: 12 }}>
            {footerInner}
          </View>
        )}
        </View>{/* cierre flexGrow */}

        {/* RESERVA: footer DESPUÉS del contenedor (anclado por el flexGrow del contenido). */}
        {!esCotizacion && (
          <View wrap={false} style={{ marginTop: 10 }}>
            {footerInner}
          </View>
        )}
      </Page>
    </Document>
  );
};

export default PDFClienteAnterior;
